import React from "react";
import {
  CalendarCheck, AlertTriangle, FileEdit, Clock, CalendarClock, BarChart3, RotateCw,
} from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { PageShell } from "../../components/PageShell";
import { SectionCard, Button, Badge, Avatar } from "../../components/primitives";
import { StatCard } from "../../components/StatCard";
import { DataTable, EmptyState } from "../../components/DataTable";
import { Input, Field } from "../../components/forms";
import { Grid, Stack, Row, Hint, StatusBadge } from "./kit";
import { useHrms, useFetch, fmtDate, fullName, monthKey } from "./useHrms";

// HRMS ▸ Attendance — derived from tracker data (DailySummary), leave and
// holidays. HR derives a range; exceptions and corrections run the cleanup.

const TABS = [
  { key: "today", label: "Today", icon: <CalendarCheck size={14} /> },
  { key: "validation", label: "Validation", icon: <AlertTriangle size={14} /> },
  { key: "corrections", label: "Corrections", icon: <FileEdit size={14} /> },
  { key: "hours", label: "Hour account", icon: <Clock size={14} /> },
  { key: "shifts", label: "Shifts & roster", icon: <CalendarClock size={14} /> },
  { key: "reports", label: "Reports", icon: <BarChart3 size={14} /> },
];

export default function HrmsAttendance() {
  const t = useDashTheme();
  const [tab, setTab] = React.useState("today");

  return (
    <PageShell
      eyebrow="HRMS"
      title="Attendance"
      subtitle="Tracked presence, exceptions and regularisation — no manual punching"
      icon={<CalendarCheck size={16} />}
      tabs={TABS}
      activeTab={tab}
      onTabChange={setTab}
    >
      {tab === "today" && <TodayTab t={t} />}
      {tab === "validation" && <ValidationTab t={t} />}
      {tab === "corrections" && <CorrectionsTab t={t} />}
      {tab === "hours" && <HoursTab t={t} />}
      {tab === "shifts" && <ShiftsTab t={t} />}
      {tab === "reports" && <ReportsTab t={t} />}
    </PageShell>
  );
}

const hrsOf = (mins) => (mins ? `${Math.floor(mins / 60)}h ${mins % 60}m` : "—");

function TodayTab({ t }) {
  const { isHr, post } = useHrms();
  const today = new Date().toISOString().slice(0, 10);
  const { data, loading, refresh } = useFetch(`/attendance/days?from=${today}&to=${today}`);
  const [deriving, setDeriving] = React.useState(false);

  const derive = async () => {
    setDeriving(true);
    try {
      const from = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
      await post("/attendance/derive", { from, to: today });
      refresh();
    } finally { setDeriving(false); }
  };

  const rows = data || [];
  const counts = rows.reduce((acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }), {});

  return (
    <Stack gap={t.s[4]}>
      <Grid min={200}>
        <StatCard label="Present" value={(counts.present || 0) + (counts.working || 0)} accent={t.status.positive} />
        <StatCard label="Half day" value={counts.half_day || 0} accent={t.status.warning} />
        <StatCard label="On leave" value={counts.on_leave || 0} accent={t.status.info} />
        <StatCard label="Absent" value={counts.absent || 0} accent={t.status.negative} />
      </Grid>

      {isHr && (
        <Hint>
          Attendance derives from tracked activity, approved leave and holidays.
          <span style={{ marginLeft: 8 }}>
            <Button size="sm" variant="secondary" leftIcon={<RotateCw size={12} />} onClick={derive} disabled={deriving}>
              {deriving ? "Deriving…" : "Derive last 7 days"}
            </Button>
          </span>
        </Hint>
      )}

      <SectionCard title={`Today · ${fmtDate(today)}`} padding={0}>
        <DataTable
          loading={loading}
          columns={[
            { key: "who", header: "Employee", render: (r) => (
              <Row gap={10}>
                <Avatar name={fullName(r.employeeId)} src={r.employeeId?.avatar} size={26} />
                <div>
                  <div style={{ fontSize: t.font.size.base, color: t.c.text }}>{fullName(r.employeeId)}</div>
                  <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>{r.employeeId?.position || ""}</div>
                </div>
              </Row>
            ) },
            { key: "shift", header: "Shift", render: (r) => r.shiftId?.name || "—" },
            { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(r.status)} /> },
            { key: "worked", header: "Hours", align: "right", render: (r) => hrsOf(r.workedMinutes) },
            { key: "flag", header: "Note", render: (r) => r.flag || "" },
          ]}
          rows={rows}
          emptyState={<EmptyState title="Nothing derived for today yet" description={isHr ? "Run derive to build today from tracker data." : "Check back after HR syncs attendance."} />}
        />
      </SectionCard>
    </Stack>
  );
}

function ValidationTab({ t }) {
  const { isHr, post } = useHrms();
  const { data, loading, refresh } = useFetch("/attendance/exceptions?resolved=false");

  const resolve = async (id) => {
    await post(`/attendance/exceptions/${id}/resolve`, {});
    refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      <Hint>Exceptions are rule-generated during derive: absences with no leave, and short-hours days below the shift threshold. Approving a correction auto-resolves its exception.</Hint>
      <SectionCard title="Open exceptions" padding={0} actions={<Badge tone={data?.length ? "warning" : "positive"}>{data?.length || 0} open</Badge>}>
        <DataTable
          loading={loading}
          columns={[
            { key: "who", header: "Employee", render: (r) => fullName(r.employeeId) },
            { key: "date", header: "Date", render: (r) => fmtDate(r.date) },
            { key: "issue", header: "Issue" },
            { key: "impact", header: "Impact", render: (r) => r.impact || "—" },
            { key: "severity", header: "Severity", render: (r) => <Badge tone={r.severity === "high" ? "negative" : r.severity === "medium" ? "warning" : "neutral"}>{r.severity}</Badge> },
            {
              key: "act", header: "", align: "right",
              render: (r) => isHr ? <Button size="sm" variant="ghost" onClick={() => resolve(r._id)}>Resolve</Button> : null,
            },
          ]}
          rows={data || []}
          emptyState={<EmptyState title="All clear" description="No unresolved attendance exceptions." />}
        />
      </SectionCard>
    </Stack>
  );
}

function CorrectionsTab({ t }) {
  const { post } = useHrms();
  const { data, loading, refresh } = useFetch("/attendance/corrections");
  const inbox = useFetch("/approvals/inbox?type=attendance_correction");

  const decide = async (id, action) => {
    await post(`/approvals/${id}/decide`, { action });
    refresh(); inbox.refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      {(inbox.data || []).length > 0 && (
        <SectionCard title="Waiting on you" padding={0} actions={<Badge tone="warning">{inbox.data.length}</Badge>}>
          <DataTable
            columns={[
              { key: "who", header: "Employee", render: (r) => fullName(r.employeeId) },
              { key: "summary", header: "Request", render: (r) => r.summary },
              { key: "when", header: "Raised", render: (r) => fmtDate(r.createdAt) },
              {
                key: "act", header: "", align: "right",
                render: (r) => (
                  <Row gap={4} justify="flex-end">
                    <Button size="sm" variant="primary" onClick={() => decide(r._id, "approve")}>Approve</Button>
                    <Button size="sm" variant="ghost" onClick={() => decide(r._id, "reject")}>Reject</Button>
                  </Row>
                ),
              },
            ]}
            rows={inbox.data}
          />
        </SectionCard>
      )}

      <SectionCard title="All corrections" subtitle="Raise one from My HR ▸ Attendance" padding={0}>
        <DataTable
          loading={loading}
          columns={[
            { key: "who", header: "Employee", render: (r) => fullName(r.employeeId) },
            { key: "date", header: "Date", render: (r) => fmtDate(r.date) },
            { key: "requestedChange", header: "Requested change" },
            { key: "reason", header: "Reason", render: (r) => r.reason || "—" },
            { key: "status", header: "Status", render: (r) => <StatusBadge value={cap(r.status)} /> },
          ]}
          rows={data || []}
          emptyState={<EmptyState title="No corrections" description="Employees raise corrections from My HR when a day looks wrong." />}
        />
      </SectionCard>
    </Stack>
  );
}

function HoursTab({ t }) {
  const month = monthKey();
  const from = `${month}-01`;
  const to = new Date().toISOString().slice(0, 10);
  const { data, loading } = useFetch(`/attendance/days?from=${from}&to=${to}`);

  const rows = data || [];
  const byEmp = new Map();
  for (const r of rows) {
    const k = r.employeeId?._id || "?";
    const cur = byEmp.get(k) || { employee: r.employeeId, worked: 0, days: 0 };
    cur.worked += r.workedMinutes || 0;
    if (["present", "half_day", "working"].includes(r.status)) cur.days += 1;
    byEmp.set(k, cur);
  }
  const agg = [...byEmp.values()].sort((a, b) => b.worked - a.worked);

  return (
    <SectionCard title={`Hour account · ${month}`} subtitle="Tracked hours vs days worked this month" padding={0}>
      <DataTable
        loading={loading}
        columns={[
          { key: "who", header: "Employee", render: (r) => fullName(r.employee) },
          { key: "days", header: "Days worked", align: "right", render: (r) => r.days },
          { key: "hours", header: "Tracked hours", align: "right", render: (r) => hrsOf(r.worked) },
          { key: "avg", header: "Avg / day", align: "right", render: (r) => r.days ? hrsOf(Math.round(r.worked / r.days)) : "—" },
        ]}
        rows={agg}
        emptyState={<EmptyState title="No hour data" description="Derive attendance to build the hour account." />}
      />
    </SectionCard>
  );
}

function ShiftsTab({ t }) {
  const { isHr, post, patch } = useHrms();
  const { data, loading, refresh } = useFetch("/shifts");
  const [form, setForm] = React.useState({ name: "", startTime: "09:30", endTime: "18:30", graceMinutes: 15 });

  const add = async () => {
    if (!form.name.trim()) return;
    await post("/shifts", form);
    setForm({ name: "", startTime: "09:30", endTime: "18:30", graceMinutes: 15 });
    refresh();
  };

  return (
    <Stack gap={t.s[4]}>
      <SectionCard title="Shifts" padding={0}>
        <DataTable
          loading={loading}
          columns={[
            { key: "name", header: "Shift" },
            { key: "window", header: "Window", render: (r) => r.isFlexible ? "Flexible" : `${r.startTime} – ${r.endTime}` },
            { key: "graceMinutes", header: "Grace", align: "right", render: (r) => `${r.graceMinutes || 0}m` },
            { key: "halfDayThresholdMinutes", header: "Half-day below", align: "right", render: (r) => hrsOf(r.halfDayThresholdMinutes) },
            {
              key: "act", header: "", align: "right",
              render: (r) => isHr ? <Button size="sm" variant="ghost" onClick={async () => { await patch(`/shifts/${r._id}`, { active: false }); refresh(); }}>Retire</Button> : null,
            },
          ]}
          rows={data || []}
          emptyState={<EmptyState title="No shifts" description="Initialize HRMS defaults or add one below." />}
        />
      </SectionCard>

      {isHr && (
        <SectionCard title="Add shift" padding={16}>
          <Row gap={12} wrap align="flex-end">
            <Field label="Name"><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Evening" /></Field>
            <Field label="Start"><Input type="time" value={form.startTime} onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))} /></Field>
            <Field label="End"><Input type="time" value={form.endTime} onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))} /></Field>
            <Field label="Grace (min)"><Input type="number" value={form.graceMinutes} onChange={(e) => setForm((f) => ({ ...f, graceMinutes: Number(e.target.value) }))} /></Field>
            <Button variant="primary" onClick={add}>Add</Button>
          </Row>
        </SectionCard>
      )}
    </Stack>
  );
}

function ReportsTab({ t }) {
  const [month, setMonth] = React.useState(monthKey());
  const { data, loading } = useFetch(`/attendance/summary?month=${month}`);

  return (
    <Stack gap={t.s[4]}>
      <Row gap={12}>
        <Field label="Month"><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} /></Field>
      </Row>
      <SectionCard title={`Monthly summary · ${month}`} padding={0}>
        <DataTable
          loading={loading}
          columns={[
            { key: "who", header: "Employee", render: (r) => fullName(r.employee) },
            { key: "present", header: "Present", align: "right" },
            { key: "halfDay", header: "Half days", align: "right" },
            { key: "onLeave", header: "Leave", align: "right" },
            { key: "absent", header: "Absent", align: "right", render: (r) => <span style={{ color: r.absent ? t.status.negative : t.c.text }}>{r.absent}</span> },
            // Days no attendance source covers. Kept separate from Absent because
            // only Absent carries LOP into payroll.
            { key: "notTracked", header: "Not tracked", align: "right", render: (r) => <span style={{ color: t.c.textTertiary }}>{r.notTracked || 0}</span> },
            { key: "workedMinutes", header: "Hours", align: "right", render: (r) => hrsOf(r.workedMinutes) },
          ]}
          rows={data || []}
          emptyState={<EmptyState title="No data for this month" description="Derive attendance first." />}
        />
      </SectionCard>
    </Stack>
  );
}

const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1).replace(/_/g, " ") : "—");
